"use client";
import Link from "next/link";
import Image from "next/image";
import logo from "@public/logo.svg";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { FC, useState, useEffect } from "react";
import { toastNotify } from "@client/components/ui/toast";
import { RotateCcw, Loader2, Star, VideoOff, LogOut } from "lucide-react";
import { getMeetingStats, submitReview } from "@client/actions/review/review.actions";

/** Props for the MeetingEndedView component */
interface MeetingEndedViewProps {
  roomName: string | null;
  ended?: boolean;
  canReopen?: boolean;
  rejoinHash?: string | null;
  participantId?: string | null;
  odooOccurrenceId?: number;
  totalTime?: string | null;
}

/** Star Rating Component */
const StarRating: FC<{
  rating: number;
  onRatingChange?: (rating: number) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}> = ({ rating, onRatingChange, disabled = false, size = "md" }) => {
  const [hoverRating, setHoverRating] = useState(0);

  const sizeClasses = {
    sm: "w-5 h-5",
    md: "w-8 h-8",
    lg: "w-10 h-10"
  };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onRatingChange?.(star)}
          onMouseEnter={() => !disabled && setHoverRating(star)}
          onMouseLeave={() => !disabled && setHoverRating(0)}
          className={`transition-transform ${disabled ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating) ? "fill-yellow-400 text-yellow-400" : "fill-gray-200 text-gray-200"
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

/** Format seconds to human-readable duration */
const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
  return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} min`;
};

/** MeetingEndedView - Full screen overlay shown when meeting ends or user leaves */
export const MeetingEndedView: FC<MeetingEndedViewProps> = ({
  roomName,
  ended = false,
  canReopen = false,
  rejoinHash,
  participantId,
  odooOccurrenceId,
  totalTime: totalTimeParam
}) => {
  const t = useTranslations();
  const router = useRouter();
  const [isRejoining, setIsRejoining] = useState(false);

  // Parse totalTime from URL parameter for instant display
  const initialTotalTime = totalTimeParam ? parseInt(totalTimeParam, 10) : null;

  // Stats loading state - start as false if we have totalTime from URL
  const [isLoadingStats, setIsLoadingStats] = useState(!totalTimeParam);
  const [totalTime, setTotalTime] = useState<number | null>(initialTotalTime);

  // Review state
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
  const [existingReviewId, setExistingReviewId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hostName, setHostName] = useState<string | null>(null);

  // Fetch stats and existing review on mount (skip if totalTime already provided)
  useEffect(() => {
    const fetchStats = async () => {
      if (!participantId || !roomName) {
        setIsLoadingStats(false);
        return;
      }

      // Skip fetch if we already have totalTime from URL
      if (totalTimeParam) {
        setIsLoadingStats(false);
        try {
          const response = await getMeetingStats(participantId, roomName, odooOccurrenceId);
          if (response.status && response.data) {
            setHostName(response.data.hostName);
            if (response.data.existingReview) {
              setRating(response.data.existingReview.review);
              setComment(response.data.existingReview.comment || "");
              setHasSubmittedReview(true);
              setExistingReviewId(response.data.existingReview.id);
            }
          }
        } catch (error) {
          Logger.error("MeetingEndedView", "Failed to fetch existing review:", error);
        }
        return;
      }

      // Full fetch if no optimistic data
      try {
        const response = await getMeetingStats(participantId, roomName, odooOccurrenceId);

        if (response.status && response.data) {
          setTotalTime(response.data.totalTime);
          setHostName(response.data.hostName);
          if (response.data.existingReview) {
            setRating(response.data.existingReview.review);
            setComment(response.data.existingReview.comment || "");
            setHasSubmittedReview(true);
            setExistingReviewId(response.data.existingReview.id);
          }
        }
      } catch (error) {
        Logger.error("MeetingEndedView", "Failed to fetch meeting stats:", error);
        // Ensure we stop loading state even on error
        setIsLoadingStats(false);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStats();
  }, [participantId, roomName, totalTimeParam, odooOccurrenceId]);

  const handleRejoin = () => {
    setIsRejoining(true);
    if (roomName) {
      let hashPart = rejoinHash ? decodeURIComponent(rejoinHash) : "";
      if (hashPart && !hashPart.startsWith("#") && !hashPart.startsWith("?")) {
        hashPart = `#${hashPart}`;
      }

      const targetUrl = `/rooms/${roomName}${hashPart}`;
      Logger.info("MeetingEndedView", `Rejoining room: ${targetUrl}`);

      // Use window.location.href to ensure a full state reset for the meeting
      window.location.href = targetUrl;
    } else {
      router.push("/");
    }
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toastNotify.error(t("validation.nameRequired"));
      return;
    }

    if (!participantId || !roomName) {
      toastNotify.error(t("errors.missingDetails"));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitReview({
        participantId,
        roomName,
        odooOccurrenceId,
        review: rating,
        comment: comment.trim() || undefined
      });

      if (response.status) {
        setHasSubmittedReview(true);
        setExistingReviewId(response.data?.id || null);
        setIsEditing(false);
      } else {
        toastNotify.error(response.message);
      }
    } catch (error) {
      Logger.error("MeetingEndedView", "Failed to submit review:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = ended ? t("meeting.meetingEnded") : t("meeting.youLeft");

  // Check if we can show review functionality - show if roomName exists
  // We'll handle missing participantId during submission
  const canShowReview = !!roomName;
  const canSubmitReview = !!(participantId && roomName);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-linear-to-br from-white via-slate-50 to-gray-100 text-gray-900 overflow-y-auto overflow-x-hidden relative py-20 sm:py-0">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "radial-gradient(#000000 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      ></div>

      {/* Header with Logo */}
      <div className="absolute top-0 left-0 rtl:left-auto rtl:right-0 w-full px-6 py-4 z-20 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <Image src={logo} alt="Freiburger Meet" width={180} height={32} style={{ height: "auto" }} priority />
        </Link>
      </div>

      <div className="relative z-10 flex flex-col items-center max-w-md w-full p-6 text-center animate-in fade-in zoom-in duration-300 mt-20 sm:mt-0">
        {/* Title Section */}
        <div className="flex flex-col items-center space-y-4 mb-8">
          <div className="p-4 rounded-full bg-blue-50 ring-1 ring-blue-100">
            {ended ? <VideoOff className="w-8 h-8 text-primary" /> : <LogOut className="w-8 h-8 text-primary" />}
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{title}</h1>
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100">
              <span className="text-primary font-medium text-sm sm:text-base">
                {ended ? t("controls.hostEndedByHost") : t("controls.thanksForParticipating")}
              </span>
            </div>
          </div>
        </div>

        {/* Meeting Info Card */}
        {canShowReview && (hostName || totalTime !== null || isLoadingStats) && (
          <div className="w-full mb-6 p-4 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-center gap-8 flex-wrap text-sm">
              {/* Host Name */}
              {hostName && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{t("controls.hostName")}</span>
                  <span className="text-gray-800 font-medium">{hostName}</span>
                </div>
              )}
              {/* Total Time */}
              {isLoadingStats ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{t("controls.totalTime")}</span>
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              ) : (
                totalTime !== null && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{t("controls.totalTime")}</span>
                    <span className="text-primary font-medium">{formatDuration(totalTime)}</span>
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {/* Review Section */}
        {canShowReview && (
          <div className="w-full mb-8 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-sm">
            {/* Review Section Content */}
            {hasSubmittedReview && !isEditing ? (
              // Read Only View
              <div className="flex flex-col items-center animate-in fade-in duration-300">
                <div className="flex justify-center mb-4">
                  <StarRating rating={rating} disabled={true} />
                </div>
                {comment && <p className="text-gray-600 text-sm italic mb-4">&ldquo;{comment}&rdquo;</p>}

                <div className="flex flex-col items-center gap-2 w-full">
                  <p className="text-green-600 text-sm font-medium">✓ {t("controls.thankYouFeedback")}</p>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="text-sm text-primary hover:text-primary-dark underline underline-offset-4 focus:outline-none"
                  >
                    {t("controls.updateReview")}
                  </button>
                </div>
              </div>
            ) : (
              // Edit/Submit Form
              <div className="animate-in fade-in duration-300">
                <div className="flex justify-center mb-4">
                  <StarRating rating={rating} onRatingChange={setRating} disabled={isSubmitting} />
                </div>

                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("controls.shareThoughts")}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-gray-400 disabled:opacity-50"
                  rows={3}
                />

                <div className="flex flex-col gap-2 mt-4 items-center">
                  {!canSubmitReview && rating > 0 && (
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-3 rounded-full bg-blue-50 border border-blue-100 animate-in fade-in slide-in-from-bottom-2">
                      <span className="text-primary font-medium text-sm">{t("errors.missingDetails")}</span>
                    </div>
                  )}
                  <button
                    onClick={handleSubmitReview}
                    disabled={isSubmitting || rating === 0 || !canSubmitReview}
                    className="px-5 py-2 bg-primary hover:bg-[#0088c7] text-white rounded-full font-medium transition-all duration-200 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 outline-none focus:ring-2 focus:ring-primary/50"
                    title={!canSubmitReview ? t("errors.missingDetails") : ""}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{existingReviewId ? t("common.updating") : t("common.submitting")}</span>
                      </>
                    ) : (
                      <span>{existingReviewId ? t("controls.updateReview") : t("controls.submitReview")}</span>
                    )}
                  </button>

                  {isEditing && (
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={isSubmitting}
                      className="text-sm text-gray-500 hover:text-gray-700 py-2"
                    >
                      {t("common.cancel")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col w-full gap-3 sm:flex-row sm:justify-center sm:gap-4 items-center">
          {roomName && (!ended || canReopen) && (
            <button
              onClick={handleRejoin}
              disabled={isRejoining}
              className="flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-gray-50 text-gray-700 rounded-full font-medium border border-gray-200 hover:border-gray-300 transition-all duration-200 outline-none shadow-sm focus:ring-2 focus:ring-gray-200"
            >
              {isRejoining ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t("meeting.joining")}</span>
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 transition-transform group-hover:-rotate-180" />
                  <span>{canReopen && ended ? t("meeting.rejoin") : t("common.retry")}</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingEndedView;
